import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTerminal, faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import protobuf from 'protobufjs';
import { Buffer } from 'buffer';
import './SSHTerminal.css';

const SSHTerminal = ({ sessionId, sourceToken, region, thingName, onConnectionClose, onTokenExpired }) => {
    const terminalRef = useRef(null);
    const [terminal, setTerminal] = useState(null);
    const [socket, setSocket] = useState(null);
    const socketRef = useRef(null);
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [streamId, setStreamId] = useState(null);
    const streamIdRef = useRef(null);
    const protoMessageRef = useRef(null);
    const [protoLoaded, setProtoLoaded] = useState(false);
    const connectionRetryRef = useRef(0);
    const loginStateRef = useRef({ stage: 'username', username: '', password: '', promptShown: false });
    const inputDisposableRef = useRef(null);
    const maxRetries = 5;

    const safeResizeTerminal = (term) => {
        if (!term) return;

        try {
            if (typeof term.proposeGeometry === 'function') {
                const geometry = term.proposeGeometry();
                if (geometry && geometry.cols > 0 && geometry.rows > 0) {
                    term.resize(geometry.cols, geometry.rows);
                    return;
                }
            }

            const container = terminalRef.current;
            if (!container) return;

            const width = container.clientWidth || 0;
            const height = container.clientHeight || 0;
            if (width <= 0 || height <= 0) return;

            const approxCharWidth = 9;
            const approxRowHeight = 18;
            const cols = Math.max(40, Math.floor(width / approxCharWidth));
            const rows = Math.max(12, Math.floor(height / approxRowHeight));

            term.resize(cols, rows);
        } catch (error) {
            console.error('Terminal resize failed:', error);
        }
    };

    useEffect(() => {
        if (!terminalRef.current) return;

        const Terminal = window.Terminal;
        if (!Terminal) {
            console.error('Terminal not loaded. Make sure xterm.min.js is loaded in index.html');
            return;
        }

        const term = new Terminal({
            fontSize: 14,
            fontFamily: 'Menlo, Monaco, "Courier New", monospace',
            cursorBlink: true,
            theme: {
                background: '#1e1e1e',
                foreground: '#cccccc',
                cursor: '#ffffff',
                black: '#000000',
                red: '#cd3131',
                green: '#0dbc79',
                yellow: '#e5e510',
                blue: '#2472c8',
                magenta: '#bc3fbc',
                cyan: '#11a8cd',
                white: '#e5e5e5',
                brightBlack: '#666666',
                brightRed: '#f14c4c',
                brightGreen: '#23d18b',
                brightYellow: '#f5f543',
                brightBlue: '#3b8eea',
                brightMagenta: '#d670d6',
                brightCyan: '#29b8db',
                brightWhite: '#e5e5e5'
            },
            rows: 24,
            cols: 80,
            scrollback: 1000
        });

        term.open(terminalRef.current);

        setTimeout(() => {
            safeResizeTerminal(term);
        }, 100);

        term.writeln('AWS IoT Secure Tunneling Terminal');
        term.writeln('');

        if (!sourceToken || !sessionId) {
            term.writeln('\x1b[33mNo active session.\x1b[0m');
            term.writeln('Click "Start Session" to begin.');
        } else {
            term.writeln('Session active. Initializing connection...');
        }
        term.writeln('');

        setTerminal(term);

        const handleResize = () => {
            safeResizeTerminal(term);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (inputDisposableRef.current) {
                inputDisposableRef.current.dispose();
                inputDisposableRef.current = null;
            }
            if (window.term === term) {
                delete window.startSSHyLoginPrompt;
                delete window.term;
                delete window.transport;
                delete window.ws;
            }
            if (term && term.destroy) {
                term.destroy();
            }
        };
    }, [sourceToken]);

    useEffect(() => {
        const loadProto = async () => {
            try {
                const root = await protobuf.load('/ssh/format.proto');
                const Message = root.lookupType('com.amazonaws.iot.securedtunneling.Message');
                protoMessageRef.current = Message;
                setProtoLoaded(true);
                if (terminal) {
                    terminal.writeln('\x1b[32mProtocol definition loaded\x1b[0m');
                }
            } catch (error) {
                console.error('Failed to load protobuf:', error);
                if (terminal) {
                    terminal.writeln('\x1b[31mError: Failed to load protocol definition\x1b[0m');
                    terminal.writeln(`\x1b[31m${error.message}\x1b[0m`);
                }
            }
        };

        if (terminal) {
            loadProto();
        }
    }, [terminal]);

    useEffect(() => {
        if (!sessionId || !sourceToken || !region || !terminal || !protoLoaded) {
            return;
        }

        if (socket && (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN)) {
            return;
        }

        connectToTunnel();

        return () => {
            if (socket && socket.readyState === WebSocket.OPEN) {
                socket.close();
            }
        };
    }, [sessionId, sourceToken, region, terminal, protoLoaded]);

    const connectToTunnel = async () => {
        try {
            setConnectionStatus('connecting');
            terminal.writeln('Initializing secure tunnel connection...');
            terminal.writeln('');
            terminal.writeln('\x1b[33mWaiting for device to connect...\x1b[0m');

            await new Promise(resolve => setTimeout(resolve, 5000));
            terminal.writeln('\x1b[32m✓ Device connected\x1b[0m');

            terminal.writeln('\x1b[33mEstablishing secure connection...\x1b[0m');

            // The relay uses the DynamoDB session ID to retrieve the source token
            // server-side. Do not connect directly to the AWS tunnel endpoint here.
            const wsUrl = 'wss://relay.iotd.quidelortho.com/tunnel';
            terminal.writeln('\x1b[33mConnecting to tunnel relay...\x1b[0m');

            const ws = new WebSocket(wsUrl);
            ws.binaryType = 'arraybuffer';

            ws.onopen = () => handleWebSocketOpen(ws);
            ws.onclose = (e) => handleWebSocketClose(e);
            ws.onerror = (e) => handleWebSocketError(e);
            ws.onmessage = (e) => handleWebSocketMessage(e);

            ws.sendB64 = (data) => sendB64Message(ws, data);

            socketRef.current = ws;
            setSocket(ws);

        } catch (error) {
            console.error('Connection error:', error);
            terminal.writeln(`\x1b[31mConnection error: ${error.message}\x1b[0m`);
            setConnectionStatus('error');
        }
    };

    const handleWebSocketOpen = (ws) => {
        terminal.writeln('\x1b[32m✓ Connected to relay\x1b[0m');
        terminal.writeln('\x1b[33mRequesting tunnel session...\x1b[0m');
        // The relay expects this JSON control message before it forwards AWS
        // Secure Tunneling binary frames.
        ws.send(JSON.stringify({ type: 'start', sessionId }));
    };

    const handleWebSocketClose = (e) => {
        socketRef.current = null;
        terminal.writeln('');

        if (e.code === 1008) {
            terminal.writeln('\x1b[31m✖ Authentication failed\x1b[0m');
            terminal.writeln('\x1b[33mYour session has expired. Please start a new session.\x1b[0m');

            if (onTokenExpired) {
                onTokenExpired();
            }
        } else if (e.code === 1006) {
            connectionRetryRef.current += 1;

            if (connectionRetryRef.current < maxRetries) {
                terminal.writeln('\x1b[33m⚠ Device not ready yet. Retrying in 3 seconds...\x1b[0m');
                terminal.writeln(`\x1b[90mAttempt ${connectionRetryRef.current + 1}/${maxRetries}\x1b[0m`);

                setTimeout(() => {
                    terminal.writeln('');
                    terminal.writeln('\x1b[33mRetrying connection...\x1b[0m');
                    connectToTunnel();
                }, 3000);
            } else {
                terminal.writeln('\x1b[31m✖ Connection failed after multiple attempts\x1b[0m');
                terminal.writeln('\x1b[33mDevice might not be connected. Please try again.\x1b[0m');
                setConnectionStatus('error');
            }
        } else {
            terminal.writeln('\x1b[33mConnection closed\x1b[0m');
        }

        setConnectionStatus('disconnected');

        if (onConnectionClose) {
            onConnectionClose();
        }
    };

    const handleWebSocketError = (e) => {
        console.error('WebSocket error:', e);
        terminal.writeln('\x1b[31m✖ Connection error\x1b[0m');
        terminal.writeln('\x1b[33mAn error occurred while connecting to the device.\x1b[0m');
        setConnectionStatus('error');
    };

    const handleWebSocketMessage = (e) => {
        try {
            // Relay control messages are text JSON; AWS tunnel protocol frames are binary.
            if (typeof e.data === 'string') {
                let controlMessage;
                try {
                    controlMessage = JSON.parse(e.data);
                } catch {
                    console.error('Received non-JSON text from tunnel relay:', e.data);
                    return;
                }

                if (controlMessage.type === 'ready') {
                    terminal.writeln('\x1b[32m✓ Secure tunnel is ready\x1b[0m');
                    terminal.writeln('');
                    setConnectionStatus('connected');
                    connectionRetryRef.current = 0;
                    const ws = socketRef.current;
                    if (ws && ws.readyState === WebSocket.OPEN) {
                        initializeSSHClient(ws);
                        startStream(ws);
                    }
                    return;
                }

                if (controlMessage.type === 'error') {
                    const message = controlMessage.message || controlMessage.error || 'Relay rejected the tunnel session';
                    terminal.writeln(`\x1b[31m✖ Tunnel relay error: ${message}\x1b[0m`);
                    setConnectionStatus('error');
                    return;
                }

                console.warn('Unknown tunnel relay control message:', controlMessage);
                return;
            }

            const Message = protoMessageRef.current;
            if (!Message) return;

            const bytes = e.data instanceof ArrayBuffer
                ? new Uint8Array(e.data)
                : ArrayBuffer.isView(e.data)
                    ? new Uint8Array(e.data.buffer, e.data.byteOffset, e.data.byteLength)
                    : null;
            if (!bytes || bytes.byteLength < 3) {
                console.warn('Ignoring invalid/short tunnel frame');
                return;
            }

            const decodedMessage = Message.decode(bytes.slice(2));
            const messageType = decodedMessage.type;

            switch (messageType) {
                case 1: {
                    // Protobuf bytes fields are already binary data.
                    if (
                        decodedMessage.payload &&
                        window.transport &&
                        window.transport.parceler
                    ) {
                        const payload = Buffer
                            .from(decodedMessage.payload)
                            .toString('latin1');

                        window.transport.parceler.handle(payload);
                    }
                    break;
                }

                case 2: {
                    setStreamId(decodedMessage.streamId);
                    streamIdRef.current = decodedMessage.streamId;
                    break;
                }

                case 3: {
                    terminal.writeln('\x1b[33mStream reset by server\x1b[0m');
                    break;
                }

                default:
                    console.warn('Unknown tunnel message type:', messageType);
                    break;

            }
        } catch (error) {
            console.error('Error handling WebSocket message:', error);
        }
    };

    const sendB64Message = (ws, data) => {
        try {
            const Message = protoMessageRef.current;
            if (!Message || !ws || ws.readyState !== WebSocket.OPEN) return;

            let dataType;
            let payloadBuffer = null;

            if (typeof data === 'number') {
                dataType = data;
            } else {
                dataType = 1;
                payloadBuffer = Buffer.from(btoa(data), 'base64');
            }

            const iotSecureObject = {
                type: dataType,
                streamId: streamIdRef.current || streamId || 0,
                ignorable: true,
                payload: payloadBuffer
            };

            const encodedProtoMessage = Message.encode(iotSecureObject).finish();
            const msgLength = encodedProtoMessage.byteLength;
            const protoMsg = new Uint8Array(2 + msgLength);

            protoMsg.set(new Uint8Array([Math.floor(msgLength / 256), msgLength % 256]));
            protoMsg.set(encodedProtoMessage, 2);

            ws.send(protoMsg);
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    const startStream = (ws) => {
        const newStreamId = Math.floor(Math.random() * 10000);
        setStreamId(newStreamId);
        streamIdRef.current = newStreamId;

        if (ws.sendB64) {
            ws.sendB64(2);
        }
    };

    const initializeSSHClient = (ws) => {
        if (window.SSHyClient) {
            const settings = new window.SSHyClient.settings();
            const transport = new window.SSHyClient.Transport(ws, settings);

            window.ws = ws;
            window.transport = transport;
            window.term = terminal;

            transport.settings.rsaCheckEnabled = false;

            // SSHyClient's legacy startxtermjs() function is not loaded by this
            // React page. Use xterm's supported onData API to handle login and
            // interactive shell input instead of assigning textarea.onkeydown.
            window.startSSHyLoginPrompt = () => {
                if (!window.transport || !window.transport.auth || window.transport.auth.authenticated) {
                    return;
                }

                const login = loginStateRef.current;
                if (login.promptShown) return;

                login.stage = 'username';
                login.username = '';
                login.password = '';
                login.promptShown = true;
                terminal.write('Login as: ');
                terminal.focus();
            };

            window.startSSHyPasswordRetry = () => {
                const currentTransport = window.transport;

                if (!currentTransport || !currentTransport.auth ||
                    currentTransport.auth.authenticated) {
                    return;
                }

                const auth = currentTransport.auth;
                const login = loginStateRef.current;

                // Preserve the username; reset only the password.
                login.stage = 'password';
                login.username = auth.termUsername || login.username;
                login.password = '';
                login.promptShown = true;

                terminal.write(
                    "\r\n" +
                    login.username +
                    "@" +
                    (auth.hostname || "device") +
                    "'s password: "
                );

                terminal.focus();
            };

            if (inputDisposableRef.current) {
                inputDisposableRef.current.dispose();
            }

            const handleTerminalInput = (data) => {
                const currentTransport = window.transport;
                if (!currentTransport || !currentTransport.auth) return;

                const auth = currentTransport.auth;
                const login = loginStateRef.current;

                if (auth.authenticated) {
                    if (currentTransport.expect_key) {
                        currentTransport.expect_key(data);
                    }
                    return;
                }

                if (auth.awaitingAuthentication) return;

                // Ctrl+C cancels the current login input without sending it.
                if (data === '\u0003') {
                    login.stage = 'username';
                    login.username = '';
                    login.password = '';
                    auth.termUsername = '';
                    auth.termPassword = undefined;
                    terminal.write('\r\nLogin as: ');
                    login.promptShown = true;
                    return;
                }

                if (data === '\r' || data === '\n') {
                    if (login.stage === 'username') {
                        if (!login.username.trim()) {
                            terminal.write('\r\nLogin as: ');
                            return;
                        }

                        auth.termUsername = login.username;
                        login.stage = 'password';
                        terminal.write('\r\n' + login.username + '@' + (auth.hostname || 'device') + "'s password: ");
                        return;
                    }

                    if (login.stage === 'password') {
                        auth.termUsername = login.username;
                        auth.termPassword = login.password;
                        login.promptShown = false;
                        terminal.write('\r\n');
                        auth.ssh_connection();
                    }
                    return;
                }

                if (data === '\u007f' || data === '\b') {
                    if (login.stage === 'username' && login.username.length > 0) {
                        login.username = login.username.slice(0, -1);
                        terminal.write('\b \b');
                    } else if (login.stage === 'password' && login.password.length > 0) {
                        login.password = login.password.slice(0, -1);
                    }
                    return;
                }

                // Do not echo the password to the terminal.
                if (data.length === 1 && data >= ' ' && data !== '\u007f') {
                    if (login.stage === 'username') {
                        login.username += data;
                        terminal.write(data);
                    } else if (login.stage === 'password') {
                        login.password += data;
                    }
                }
            };

            terminal.on('data', handleTerminalInput);

            terminal.focus();
            terminal.writeln('SSH client initialized');
            terminal.writeln('');
        } else {
            terminal.writeln('\x1b[31mError: SSH client not loaded\x1b[0m');
        }
    };

    const getStatusColor = () => {
        switch (connectionStatus) {
            case 'connected':
                return 'connected';
            case 'connecting':
                return 'connecting';
            case 'error':
                return 'error';
            default:
                return '';
        }
    };

    const getStatusText = () => {
        switch (connectionStatus) {
            case 'connected':
                return 'Connected';
            case 'connecting':
                return 'Connecting...';
            case 'error':
                return 'Connection Error';
            case 'disconnected':
                return 'Disconnected';
            default:
                return 'Ready';
        }
    };

    if (!sourceToken || !sessionId) {
        return (
            <div className="ssh-terminal-container">
                <div className="ssh-terminal-header">
                    <div className="ssh-terminal-title">
                        <FontAwesomeIcon icon={faTerminal} className="me-2" />
                        SSH Terminal
                    </div>
                </div>
                <div className="ssh-terminal-body">
                    <div className="ssh-terminal-placeholder">
                        <div className="ssh-terminal-placeholder-icon">
                            <FontAwesomeIcon icon={faTerminal} />
                        </div>
                        <div className="ssh-terminal-placeholder-text">
                            Start a tunnel session to connect to the device
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const handleReconnect = () => {
        if (!terminal || !sourceToken || !sessionId) return;

        if (socket && socket.readyState === WebSocket.OPEN && connectionStatus === 'connected') {
            terminal.writeln('');
            terminal.writeln('\x1b[33m━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\x1b[0m');
            terminal.writeln('\x1b[36mAlready connected to device\x1b[0m');
            terminal.writeln('\x1b[33m━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\x1b[0m');
            terminal.writeln('');
            return;
        }

        terminal.writeln('');
        terminal.writeln('\x1b[33m━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\x1b[0m');
        terminal.writeln('\x1b[36mManual reconnection initiated...\x1b[0m');
        terminal.writeln('\x1b[33m━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\x1b[0m');
        terminal.writeln('');

        connectionRetryRef.current = 0;

        if (socket) {
            socket.close();
            setTimeout(() => {
                connectToTunnel();
            }, 500);
        } else {
            connectToTunnel();
        }
    };

    return (
        <div className="ssh-terminal-container">
            <div className="ssh-terminal-header">
                <div className="ssh-terminal-title">
                    <FontAwesomeIcon icon={faTerminal} className="me-2" />
                    SSH Terminal - {thingName}
                </div>
                <div className="ssh-terminal-actions">
                    {connectionStatus === 'error' || connectionStatus === 'disconnected' ? (
                        <button
                            type="button"
                            onClick={handleReconnect}
                            className="btn btn-sm btn-outline-primary"
                            title="Retry connection"
                        >
                            <FontAwesomeIcon icon={faSyncAlt} className="me-1" />
                            Reconnect
                        </button>
                    ) : null}
                    <div className="ssh-terminal-status">
                        <div className={`ssh-status-indicator ${getStatusColor()}`}></div>
                        <span className="ssh-status-text">{getStatusText()}</span>
                    </div>
                </div>
            </div>
            <div className="ssh-terminal-body">
                <div ref={terminalRef} style={{ height: '100%', width: '100%' }}></div>
            </div>
        </div>
    );
};

export default SSHTerminal;
